
import { useEffect, useState, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { User } from '@supabase/supabase-js';

// Profile update cache to prevent redundant profile updates
const profileUpdateCache = new Map<string, number>();
const PROFILE_UPDATE_COOLDOWN = 60000; // 1 minute cooldown

export const useSupabaseUser = () => {
  const [user, setUser] = useState<User | null>(null);
  const profileUpdateInProgressRef = useRef(false);

  useEffect(() => {
    // Get initial user
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        setUser(session.user);
        // Ensure the user's profile is up to date
        updateUserProfile(session.user);
      } else {
        setUser(null);
      }
    });

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        setUser(session.user);
        // Update profile when user auth state changes
        updateUserProfile(session.user);
      } else {
        setUser(null);
      }
    });

    // Function to update or create user profile with complete information
    const updateUserProfile = async (user: User) => {
      // Check if update already in progress
      if (profileUpdateInProgressRef.current) return;
      
      // Check if we recently updated this profile
      const lastUpdate = profileUpdateCache.get(user.id);
      const now = Date.now();
      if (lastUpdate && (now - lastUpdate < PROFILE_UPDATE_COOLDOWN)) {
        console.log("Profile was recently updated, skipping redundant update");
        return;
      }
      
      profileUpdateInProgressRef.current = true;
      
      try {
        console.log("Checking user profile for:", user.id);
        
        // Check if profile exists using a single query
        const { data: existingProfile, error: fetchError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single();
          
        if (fetchError && fetchError.code !== 'PGRST116') {
          // PGRST116 is "not found" - which we expect if the profile doesn't exist
          console.error("Error fetching user profile:", fetchError);
          return;
        }
        
        if (!existingProfile) {
          // Create new profile if it doesn't exist
          await createNewProfile(user);
        } else if (!isProfileComplete(existingProfile)) {
          // Update existing profile only if it's incomplete
          await updateIncompleteProfile(user, existingProfile);
        } else {
          console.log("User profile exists and has complete information: ", existingProfile.full_name);
        }
        
        // Update the cache timestamp
        profileUpdateCache.set(user.id, now);
      } catch (error) {
        console.error("Error managing user profile:", error);
      } finally {
        profileUpdateInProgressRef.current = false;
      }
    };
    
    const createNewProfile = async (user: User) => {
      console.log("Creating new profile for user:", user.id);
      
      // Check if user is admin
      const { data: isAdmin } = await supabase.rpc('has_role', {
        user_id: user.id,
        _role: 'admin'
      });
      
      // Extract name from metadata with fallbacks
      let fullName = user.user_metadata?.full_name || 
                  user.user_metadata?.name || 
                  `${user.user_metadata?.first_name || ''} ${user.user_metadata?.last_name || ''}`.trim() ||
                  user.email?.split('@')[0];
                  
      // Capitalize email username if that's all we have
      if (fullName && fullName === user.email?.split('@')[0]) {
        fullName = fullName.charAt(0).toUpperCase() + fullName.slice(1);
      }
      
      // If user is admin, ensure they have "Admin" in their name
      if (isAdmin) {
        fullName = fullName ? `${fullName} (Admin)` : 'Admin User';
      } else if (!fullName) {
        fullName = 'User';
      }
                  
      const { error: insertError } = await supabase
        .from('profiles')
        .insert({
          id: user.id,
          email: user.email,
          full_name: fullName,
          first_name: user.user_metadata?.first_name || user.user_metadata?.given_name,
          last_name: user.user_metadata?.last_name || user.user_metadata?.family_name,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });
        
      if (insertError) {
        console.error("Error creating user profile:", insertError);
      } else {
        console.log("Successfully created profile with full_name:", fullName);
      }
    };
    
    const updateIncompleteProfile = async (user: User, profile: any) => {
      console.log("Updating profile with more complete information for user:", user.id);
      
      // Check if user is admin
      const { data: isAdmin } = await supabase.rpc('has_role', {
        user_id: user.id,
        _role: 'admin'
      });
      
      // Extract name from metadata with multiple fallbacks
      let fullName = user.user_metadata?.full_name || 
                  user.user_metadata?.name || 
                  `${user.user_metadata?.first_name || ''} ${user.user_metadata?.last_name || ''}`.trim() ||
                  profile.email?.split('@')[0] || 
                  user.email?.split('@')[0];
      
      // Capitalize email username if that's all we have
      if (fullName && (fullName === profile.email?.split('@')[0] || 
                      fullName === user.email?.split('@')[0])) {
        fullName = fullName.charAt(0).toUpperCase() + fullName.slice(1);
      }
      
      // If user is admin, ensure they have "Admin" in their name
      if (isAdmin) {
        fullName = fullName ? `${fullName} (Admin)` : 'Admin User';
      } else if (!fullName) {
        fullName = 'User';
      }
      
      const updates = {
        full_name: fullName,
        first_name: user.user_metadata?.first_name || user.user_metadata?.given_name || profile.first_name,
        last_name: user.user_metadata?.last_name || user.user_metadata?.family_name || profile.last_name,
        email: user.email || profile.email,
        updated_at: new Date().toISOString()
      };
      
      // Only update fields that have values
      const finalUpdates = Object.fromEntries(
        Object.entries(updates).filter(([_, v]) => v !== undefined && v !== null && v !== '')
      );
      
      const { error: updateError } = await supabase
        .from('profiles')
        .update(finalUpdates)
        .eq('id', user.id);
        
      if (updateError) {
        console.error("Error updating user profile:", updateError);
      } else {
        console.log("Successfully updated profile with full_name:", fullName);
      }
    };
    
    const isProfileComplete = (profile: any): boolean => {
      return (
        profile.full_name && 
        profile.full_name !== 'Unknown User' && 
        !profile.full_name.startsWith('User-')
      );
    };

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  return { user };
};
